import type { ComponentType } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { JournalEntryDetailBody, type JournalEntryDetail } from './JournalEntryDetailBody';
import { InvoiceDetailBody, type InvoiceDetail } from './InvoiceDetailBody';
import { SaleReturnDetailBody, type SaleReturnDetail } from './SaleReturnDetailBody';
import { PurchaseOrderDetailBody, type PurchaseOrderDetail } from './PurchaseOrderDetailBody';
import { VendorPaymentDetailBody, type VendorPaymentDetail } from './VendorPaymentDetailBody';
import { StockTransferDetailBody, type StockTransferDetail } from './StockTransferDetailBody';
import { StockTakeDetailBody, type StockTakeDetail } from './StockTakeDetailBody';
import { ExpenseDetailBody, type ExpenseDetail } from './ExpenseDetailBody';
import { ChequeDetailBody, type ChequeDetail } from './ChequeDetailBody';
import { AuditEntryDetailBody, type AuditEntryDetail } from './AuditEntryDetailBody';
import { StockMovementDetailBody, type StockMovementDetail } from './StockMovementDetailBody';

/** Record types of the viewer (camelCase, shared names; docs/CONTROLS-PLAN.md "Record references"). */
export type RecordType = 'journalEntry' | 'invoice' | 'saleReturn' | 'purchaseOrder' | 'vendorPayment' | 'stockMovement' | 'auditEntry' | 'stockTransfer' | 'stockTake' | 'expense' | 'cheque';

export interface RecordRef {
  type: RecordType;
  id: string;
  /** Full number shown to the user (never truncated). */
  label: string;
  context?: Record<string, string>;
}

export interface RecordQueryResult<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => unknown;
}

export interface RegistryEntry<T = unknown> {
  /** key in the 'records' namespace */
  labelKey: string;
  /** permission key, or 'a | b' for any-of. Display check only; the backend enforces it. */
  permission: string;
  useData: (id: string) => RecordQueryResult<T>;
  /** status shown as a badge in the header */
  status?: (data: T) => string | null | undefined;
  Body: ComponentType<{ data: T }>;
}

function useJournalEntry(id: string): RecordQueryResult<JournalEntryDetail> {
  return useQuery({
    queryKey: ['record-viewer', 'journalEntry', id],
    queryFn: async () => (await api.get<JournalEntryDetail>(`/journal-entries/${id}`)).data,
    retry: false,
  });
}

/** One by-id fetch per record type (read-only; the backend enforces tenant and permission). */
function byId<T>(type: RecordType, path: (id: string) => string) {
  return (id: string): RecordQueryResult<T> =>
    // eslint-disable-next-line react-hooks/rules-of-hooks -- called once per render of RecordContent for a fixed type
    useQuery({
      queryKey: ['record-viewer', type, id],
      queryFn: async () => (await api.get<T>(path(id))).data,
      retry: false,
    });
}

const journalEntryEntry: RegistryEntry<JournalEntryDetail> = {
  labelKey: 'typeJournalEntry',
  permission: 'accounting:read',
  useData: useJournalEntry,
  status: (d) => d.status,
  Body: JournalEntryDetailBody,
};

const entry = <T,>(e: RegistryEntry<T>) => e as unknown as RegistryEntry<unknown>;

const REGISTRY: Record<RecordType, RegistryEntry<unknown>> = {
  journalEntry: journalEntryEntry as unknown as RegistryEntry<unknown>,
  // POS sale = the shared 'invoice' type. Permission is the same as the backend by-id route.
  invoice: entry<InvoiceDetail>({
    labelKey: 'typeInvoice',
    permission: 'pos:read',
    useData: byId<InvoiceDetail>('invoice', (id) => `/sales/${id}`),
    status: (d) => d.status,
    Body: InvoiceDetailBody,
  }),
  saleReturn: entry<SaleReturnDetail>({
    labelKey: 'typeSaleReturn',
    permission: 'pos:read',
    useData: byId<SaleReturnDetail>('saleReturn', (id) => `/sales/returns/${id}`),
    status: (d) => d.status,
    Body: SaleReturnDetailBody,
  }),
  purchaseOrder: entry<PurchaseOrderDetail>({
    labelKey: 'typePurchaseOrder',
    permission: 'purchases:read',
    useData: byId<PurchaseOrderDetail>('purchaseOrder', (id) => `/purchases/${id}`),
    status: (d) => d.status,
    Body: PurchaseOrderDetailBody,
  }),
  vendorPayment: entry<VendorPaymentDetail>({
    labelKey: 'typeVendorPayment',
    permission: 'bank:read | purchases:read',
    useData: byId<VendorPaymentDetail>('vendorPayment', (id) => `/vendor-payments/${id}`),
    status: (d) => d.status,
    Body: VendorPaymentDetailBody,
  }),
  stockMovement: entry<StockMovementDetail>({
    labelKey: 'typeStockMovement',
    permission: 'inventory:read',
    useData: byId<StockMovementDetail>('stockMovement', (id) => `/inventory/movements/${id}`),
    Body: StockMovementDetailBody,
  }),
  stockTransfer: entry<StockTransferDetail>({
    labelKey: 'typeStockTransfer',
    permission: 'transfers:read',
    useData: byId<StockTransferDetail>('stockTransfer', (id) => `/stock-transfers/${id}`),
    status: (d) => d.status,
    Body: StockTransferDetailBody,
  }),
  stockTake: entry<StockTakeDetail>({
    labelKey: 'typeStockTake',
    permission: 'inventory:read',
    useData: byId<StockTakeDetail>('stockTake', (id) => `/stock-takes/${id}`),
    status: (d) => d.status,
    Body: StockTakeDetailBody,
  }),
  expense: entry<ExpenseDetail>({
    labelKey: 'typeExpense',
    permission: 'expenses:read',
    useData: byId<ExpenseDetail>('expense', (id) => `/expenses/${id}`),
    status: (d) => d.status,
    Body: ExpenseDetailBody,
  }),
  cheque: entry<ChequeDetail>({
    labelKey: 'typeCheque',
    permission: 'cheques:read',
    useData: byId<ChequeDetail>('cheque', (id) => `/cheques/${id}`),
    status: (d) => d.status,
    Body: ChequeDetailBody,
  }),
  auditEntry: entry<AuditEntryDetail>({
    labelKey: 'typeAuditEntry',
    permission: 'audit:read',
    useData: byId<AuditEntryDetail>('auditEntry', (id) => `/audit-logs/${id}`),
    Body: AuditEntryDetailBody,
  }),
};

/** Lookup is lazy so the registry and the bodies (which render RecordLinks) can import each other. */
export function getRegistryEntry(type: string): RegistryEntry<unknown> | null {
  return (REGISTRY as Record<string, RegistryEntry<unknown> | undefined>)[type] ?? null;
}

/** 'a | b' = any of; wildcards are handled by the auth store's hasPermission. */
export function hasRecordPermission(permission: string, has: (p: string) => boolean): boolean {
  return permission.split('|').some((p) => has(p.trim()));
}
